import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, ShieldCheck, Send, Lock } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
      toast.success('Reset link sent! Check your inbox.');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send reset link. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      toast.success('Reset link resent!');
    } catch (err) {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared styles ───
  const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    marginBottom: 6,
    textAlign: 'left',
  };

  const inputFieldStyle = {
    width: '100%',
    padding: '14px 16px',
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-base)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  // ─── Value Panel (Left) ───
  const valuePanelContent = (
    <div style={{
      background: 'linear-gradient(160deg, #0A1628 0%, #0F1D3A 40%, #132244 100%)',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 'clamp(32px, 6vw, 48px)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Geometric mesh overlay */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 30% 20%, #2563EB 1px, transparent 1px),
                          radial-gradient(circle at 70% 80%, #10B981 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />

      {/* Ambient orb */}
      <div style={{
        position: 'absolute',
        width: 'min(350px, 70vw)', height: 'min(350px, 70vw)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
        top: '15%', right: '-15%',
        animation: 'glowPulse 4s ease-in-out infinite',
      }} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 380, width: '100%' }}>
        {/* Large lock icon */}
        <div style={{
          width: 80, height: 80,
          borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.1)',
          border: '2px solid rgba(16, 185, 129, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto var(--space-2xl)',
        }}>
          <Lock size={36} color="#10B981" />
        </div>

        {/* Quote */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-xl)',
          marginBottom: 'var(--space-2xl)',
        }}>
          <p style={{
            color: '#E2E8F0',
            fontSize: 'var(--text-base)',
            lineHeight: 1.7,
            fontStyle: 'italic',
            margin: '0 0 var(--space-md)',
          }}>
            "Simple, secure, fast. I was back in my account in under 30 seconds."
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'center' }}>
            <div style={{
              width: 32, height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600,
            }}>
              M
            </div>
            <div>
              <p style={{ color: '#F1F5F9', fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>
                Michael R.
              </p>
              <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>
                CTO, ScaleUp
              </p>
            </div>
          </div>
        </div>

        {/* Security badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.15)',
          borderRadius: 'var(--radius-full)',
        }}>
          <ShieldCheck size={16} color="#10B981" />
          <span style={{ color: '#6EE7B7', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
            256-bit encrypted reset process
          </span>
        </div>
      </div>
    </div>
  );

  // ─── Form Panel (Right) ───
  const formContent = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(24px, 6vw, 48px)',
      background: 'var(--bg-primary)',
      minHeight: '100%',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
      }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          <h1 style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: '0 0 8px',
            letterSpacing: '-0.5px',
          }}>
            Forgot Your Password?
          </h1>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-sm)',
            margin: 0,
            lineHeight: 1.5,
          }}>
            No worries,  it happens to the best of us.
          </p>
        </div>

        {/* Error */}
        {error && <p className="error-message">{error}</p>}

        {/* Form or Success */}
        {!sent ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle} htmlFor="forgot-email">Email address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{
                  position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)', pointerEvents: 'none',
                }} />
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@company.com"
                  style={{ ...inputFieldStyle, paddingLeft: 42 }}
                  required
                  autoComplete="email"
                  autoFocus
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent-primary)';
                    e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--input-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-button auth-button-primary"
              style={{
                height: 50,
                fontSize: 'var(--text-base)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
              }}
            >
              {loading ? (
                <>
                  <span className="spinner" /> Sending...
                </>
              ) : (
                <>
                  <Send size={18} /> Send Reset Link
                </>
              )}
            </button>
          </form>
        ) : (
          /* ─── Success State ─── */
          <div style={{ animation: 'scaleIn 0.35s ease' }}>
            <div style={{
              width: 56, height: 56,
              borderRadius: '50%',
              background: 'var(--success-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-lg)',
            }}>
              <Send size={24} color="var(--success)" />
            </div>

            <h2 style={{
              color: 'var(--text-primary)',
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              textAlign: 'center',
              margin: '0 0 8px',
            }}>
              Check Your Email
            </h2>

            {/* Email display card */}
            <div style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <Mail size={18} color="var(--accent-primary)" />
              <span style={{
                color: 'var(--text-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 500,
                wordBreak: 'break-all',
              }}>
                {email}
              </span>
            </div>

            <p style={{
              color: 'var(--text-tertiary)',
              fontSize: 'var(--text-sm)',
              textAlign: 'center',
              margin: '0 0 4px',
              lineHeight: 1.5,
            }}>
              Didn't receive it? Check spam or
            </p>
            <button
              onClick={handleResend}
              disabled={loading}
              style={{
                display: 'block',
                margin: '0 auto 24px',
                background: 'none',
                border: 'none',
                color: 'var(--accent-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {loading ? 'Sending...' : 'Resend email'}
            </button>
          </div>
        )}

        {/* Back to login,  inside a contained row */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: sent ? 0 : 24,
          paddingTop: sent ? 0 : 0,
        }}>
          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              textDecoration: 'none',
              padding: '10px 20px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)',
              transition: 'all 0.2s ease',
              background: 'var(--bg-secondary)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent-primary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-primary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <ArrowLeft size={16} /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );

  // ─── Render ───
  return (
    <>
      {/* Desktop: split screen */}
      <div className="auth-container" style={{ display: 'none' }} data-desktop>
        <div style={{ width: '40%', height: '100%' }}>{valuePanelContent}</div>
        <div style={{ width: '60%', height: '100%', overflow: 'auto' }}>{formContent}</div>
      </div>

      {/* Mobile: single column */}
      <div className="auth-container" style={{ flexDirection: 'column' }} data-mobile>
        <style>{`
          @media (min-width: 769px) {
            [data-mobile] { display: none !important; }
            [data-desktop] { display: flex !important; }
          }
          @media (max-width: 768px) {
            [data-mobile] { display: flex !important; }
            [data-desktop] { display: none !important; }
          }
        `}</style>
        <div style={{ flex: 1, overflow: 'auto' }}>{formContent}</div>
      </div>
    </>
  );
}
